// src/components/roads/QuickWorkOrderModal.js
import React, { useState, useEffect } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    FormControl,
    FormLabel,
    Textarea,
    Input,
    Select,
    Button,
    HStack,
    Box,
    Text,
    Icon,
    VStack,
    Flex,
    Badge,
    useColorModeValue,
    Tooltip,
    Divider,
    Alert,
    AlertIcon,
    Spinner
} from '@chakra-ui/react';
import { MdBuild, MdOutlineAutoAwesome } from 'react-icons/md';
import { FaBrain } from 'react-icons/fa';

const QuickWorkOrderModal = ({ isOpen, onClose, road, onSubmit, initialValues = {} }) => {
    // 🎨 FlowSense Theme Variables
    const modalBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const aiBannerBg = useColorModeValue('purple.50', 'purple.900');
    const aiBannerBorder = useColorModeValue('purple.200', 'purple.600');
    const isAiGenerated = !!initialValues.title;

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [priority, setPriority] = useState('medium');
    const [category, setCategory] = useState('Road Paving');
    const [workOrderType, setWorkOrderType] = useState('corrective'); // 🚀 Support for Inspection, Paving, etc.
    const [dueDate, setDueDate] = useState('');
    const [slaDeadline, setSlaDeadline] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [isGeneratingAiBrief, setIsGeneratingAiBrief] = useState(false);

    // 🚀 Safe Date Formatters for HTML Inputs
    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        try { return new Date(dateString).toISOString().split('T')[0]; }
        catch { return ''; }
    };

    const formatDateTimeForInput = (dateString) => {
        if (!dateString) return '';
        try { return new Date(dateString).toISOString().slice(0, 16); }
        catch { return ''; }
    };

    // 🚀 Frontend SLA Calculator matching utility targets
    const calculateAndSetSLA = (selectedPriority) => {
        const targetDate = new Date();
        switch (selectedPriority) {
            case 'critical':
                targetDate.setHours(targetDate.getHours() + 24);
                break;
            case 'high':
                targetDate.setHours(targetDate.getHours() + 72);
                break;
            case 'medium':
                targetDate.setDate(targetDate.getDate() + 7);
                break;
            case 'low':
            default:
                targetDate.setDate(targetDate.getDate() + 14);
                break;
        }

        const calculatedSlaString = formatDateTimeForInput(targetDate.toISOString());
        setSlaDeadline(calculatedSlaString);
        setDueDate(calculatedSlaString.split('T')[0]);
    };

    // Reset/Pre-fill the form whenever the modal opens
    useEffect(() => {
        if (isOpen) {
            const roadIdentifier = road?.name || road?.metadata?.road_code || road?.id || 'Road Segment';
            const defaultTitle = `Road Maintenance: ${roadIdentifier}`;

            setTitle(initialValues.title || defaultTitle);
            setDescription(initialValues.description || `Scheduled road works and inspection for ${roadIdentifier}.`);
            
            const initPriority = initialValues.priority || 'medium';
            setPriority(initPriority);
            setCategory(initialValues.category || 'Road Paving');
            setWorkOrderType(initialValues.work_order_type || 'corrective');
            
            if (initialValues.slaDeadline) {
                setSlaDeadline(formatDateTimeForInput(initialValues.slaDeadline));
                setDueDate(formatDateForInput(initialValues.dueDate || initialValues.slaDeadline));
            } else {
                calculateAndSetSLA(initPriority);
            }
        }
    }, [isOpen, initialValues, road]);

    // Handle manual priority changes to re-calculate SLA dynamically
    const handlePriorityChange = (e) => {
        const newPriority = e.target.value;
        setPriority(newPriority);
        calculateAndSetSLA(newPriority);
    };

    // 🚀 Trigger AI assistance to enrich description
    const handleEnhanceWithAI = async () => {
        setIsGeneratingAiBrief(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 800)); // Simulated AI synthesis
            const roadName = road?.name || 'this road segment';
            setDescription(prev => `${prev}\n\n🤖 FlowSense AI Diagnostic Assessment:\nRecommended immediate intervention on ${roadName} due to surface degradation risk and traffic load parameters. Execute specified quality assurance protocols.`);
        } finally {
            setIsGeneratingAiBrief(false);
        }
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            await onSubmit({ 
                title, 
                description, 
                priority, 
                category, 
                domain: 'road',
                work_order_type: workOrderType,
                road_id: road?.id || road?.road_id || null,
                location_description: road?.name || road?.metadata?.road_code || null,
                due_date: dueDate || null, 
                sla_deadline: slaDeadline ? new Date(slaDeadline).toISOString() : null 
            });
            onClose();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered scrollBehavior="inside">
            <ModalOverlay backdropFilter="blur(6px)" />
            <ModalContent borderRadius="2xl" bg={modalBg} borderWidth="1px" borderColor={isAiGenerated ? 'pink.300' : borderColor} boxShadow={isAiGenerated ? '0 0 20px rgba(213, 63, 140, 0.15)' : 'xl'}>
                <ModalHeader borderBottomWidth="1px" borderColor={borderColor} pb={4}>
                    <Flex justify="space-between" align="flex-start">
                        <HStack spacing={3}>
                            <Box bg={isAiGenerated ? "pink.500" : "purple.500"} p={2.5} borderRadius="xl" boxShadow="sm">
                                <Icon as={isAiGenerated ? MdOutlineAutoAwesome : MdBuild} color="white" boxSize={5} />
                            </Box>
                            <Box>
                                <HStack>
                                    <Text fontSize="md" fontWeight="800">Dispatch Road Work Order</Text>
                                    {isAiGenerated && (
                                        <Badge colorScheme="pink" variant="subtle" borderRadius="full" px={2} fontSize="2xs">
                                            AI AUTO-FILLED
                                        </Badge>
                                    )}
                                </HStack>
                                <Text fontSize="xs" color="gray.500" fontWeight="600">
                                    Target Road: {road?.name || road?.metadata?.road_code || road?.id || 'Unknown Road'}
                                </Text>
                            </Box>
                        </HStack>
                    </Flex>
                </ModalHeader>
                
                <ModalCloseButton mt={2} />
                
                <ModalBody py={5}>
                    <VStack spacing={5} align="stretch">
                        {/* 🚀 AI Assistant Integration Banner */}
                        <Alert status="info" borderRadius="xl" variant="subtle" bg={aiBannerBg} borderColor={aiBannerBorder} borderWidth="1px">
                            <AlertIcon as={FaBrain} color="purple.500" />
                            <Box fontSize="xs" flex="1">
                                <Text fontWeight="bold" color="purple.700">FlowSense AI Road System</Text>
                                <Text color="purple.600">Supports inspections, resurfacing, and pavement grading with automated SLA tracking.</Text>
                            </Box>
                            <Button size="xs" colorScheme="purple" variant="solid" onClick={handleEnhanceWithAI} isLoading={isGeneratingAiBrief} leftIcon={<MdOutlineAutoAwesome />}>
                                AI Brief
                            </Button>
                        </Alert>

                        <FormControl isRequired>
                            <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase" color="gray.500">Task Title</FormLabel>
                            <Input 
                                value={title} 
                                onChange={e => setTitle(e.target.value)} 
                                placeholder="Work Order Title"
                                borderRadius="lg"
                                bg={isAiGenerated ? 'pink.50' : 'transparent'}
                            />
                        </FormControl>

                        <HStack spacing={4}>
                            <FormControl>
                                <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase" color="gray.500">Work Order Type</FormLabel>
                                <Select value={workOrderType} onChange={e => setWorkOrderType(e.target.value)} borderRadius="lg">
                                    <option value="corrective">Corrective Repair</option>
                                    <option value="inspection">Inspection & Audit</option>
                                    <option value="preventive">Preventive Maintenance</option>
                                    <option value="emergency">Emergency Response</option>
                                    <option value="paving">Paving & Resurfacing</option>
                                </Select>
                            </FormControl>

                            <FormControl>
                                <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase" color="gray.500">Category</FormLabel>
                                <Select value={category} onChange={e => setCategory(e.target.value)} borderRadius="lg">
                                    <option value="Road Paving">Road Paving</option>
                                    <option value="Pothole Repair">Pothole Repair</option>
                                    <option value="Line Marking">Line Marking</option>
                                    <option value="Drainage">Drainage</option>
                                    <option value="Signage">Signage</option>
                                    <option value="Inspection">Inspection</option>
                                    <option value="Emergency">Emergency</option>
                                </Select>
                            </FormControl>
                        </HStack>
                        
                        <FormControl>
                            <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase" color="gray.500">Diagnostic Description</FormLabel>
                            <Textarea 
                                value={description} 
                                onChange={e => setDescription(e.target.value)}
                                rows={4} 
                                fontSize="sm" 
                                borderRadius="lg"
                                bg={isAiGenerated ? 'pink.50' : 'transparent'}
                                placeholder="Details about the required road works..."
                            />
                        </FormControl>

                        <HStack spacing={4}>
                            <FormControl>
                                <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase" color="gray.500">Priority</FormLabel>
                                <Select value={priority} onChange={handlePriorityChange} borderRadius="lg">
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                    <option value="critical">Critical</option>
                                </Select>
                            </FormControl>
                        </HStack>

                        <Divider />

                        <HStack spacing={4}>
                            <FormControl isRequired>
                                <Tooltip label="The target date for work completion" hasArrow placement="top">
                                    <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase" color="gray.500" cursor="help">
                                        Scheduled Due Date
                                    </FormLabel>
                                </Tooltip>
                                <Input 
                                    type="date" 
                                    value={dueDate} 
                                    onChange={e => setDueDate(e.target.value)} 
                                    borderRadius="lg"
                                />
                            </FormControl>

                            <FormControl>
                                <Tooltip label="Strict SLA compliance deadline enforced by the backend." hasArrow placement="top">
                                    <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase" color="red.400" cursor="help">
                                        SLA Resolution Deadline
                                    </FormLabel>
                                </Tooltip>
                                <Input 
                                    type="datetime-local" 
                                    value={slaDeadline} 
                                    onChange={e => setSlaDeadline(e.target.value)} 
                                    borderRadius="lg"
                                    borderColor={slaDeadline ? 'red.200' : 'inherit'}
                                />
                            </FormControl>
                        </HStack>
                    </VStack>
                </ModalBody>
                
                <ModalFooter borderTopWidth="1px" borderColor={borderColor} pt={4}>
                    <Button variant="ghost" mr={3} onClick={onClose} borderRadius="lg">
                        Cancel
                    </Button>
                    <Button 
                        colorScheme={isAiGenerated ? "pink" : "purple"}
                        onClick={handleSubmit} 
                        isLoading={submitting}
                        loadingText="Dispatching..."
                        isDisabled={!title || !dueDate} 
                        leftIcon={<Icon as={isAiGenerated ? MdOutlineAutoAwesome : MdBuild} />}
                        borderRadius="lg"
                    >
                        {isAiGenerated ? "Approve & Dispatch" : "Dispatch Road Work Order"}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default QuickWorkOrderModal;